// Tax return tab: compute output/input tax and the box breakdown for any
// date range. Compute-only — the backend stores no filed return, so this
// page shows the numbers behind a filing, never a filing status.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import { getTaxReturn, type TaxReturn } from "@/lib/api";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function quarterStartIso(): string {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3) * 3;
  return `${now.getFullYear()}-${String(quarter + 1).padStart(2, "0")}-01`;
}

type Basis = "accrual" | "cash";

export function ReturnTab() {
  const [start, setStart] = useState(quarterStartIso);
  const [end, setEnd] = useState(todayIso);
  const [basis, setBasis] = useState<Basis>("accrual");
  const [applied, setApplied] = useState({
    start: quarterStartIso(),
    end: todayIso(),
    basis: "accrual" as Basis,
  });
  const [report, setReport] = useState<TaxReturn | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(
    (query: { start: string; end: string; basis: Basis }) => {
      setLoading(true);
      setError(null);
      getTaxReturn({
        start_date: query.start,
        end_date: query.end,
        basis: query.basis,
      })
        .then(setReport)
        .catch((cause: unknown) => setError(toErrorMessage(cause)))
        .finally(() => setLoading(false));
    },
    [],
  );

  useEffect(() => {
    run(applied);
  }, [applied, run]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!start || !end) {
      setError("Start and end dates are required.");
      return;
    }
    if (end < start) {
      setError("End date must be on or after the start date.");
      return;
    }
    setApplied({ start, end, basis });
  };

  return (
    <div className="grid gap-4">
      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => run(applied)}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      <form
        onSubmit={submit}
        className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5"
      >
        <label htmlFor="txr-start" className="grid gap-1 text-xs font-medium">
          Start date
          <Input
            id="txr-start"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            required
            disabled={loading}
          />
        </label>
        <label htmlFor="txr-end" className="grid gap-1 text-xs font-medium">
          End date
          <Input
            id="txr-end"
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            required
            disabled={loading}
          />
        </label>
        <label htmlFor="txr-basis" className="grid gap-1 text-xs font-medium">
          Basis
          <select
            id="txr-basis"
            className="select"
            value={basis}
            onChange={(e) => setBasis(e.target.value as Basis)}
            disabled={loading}
          >
            <option value="accrual">Accrual</option>
            <option value="cash">Cash</option>
          </select>
        </label>
        <Button type="submit" disabled={loading || !start || !end}>
          {loading ? "Computing…" : "Compute return"}
        </Button>
        <p className="w-full text-xs text-[var(--muted-foreground)]">
          A calculation for the file-yourself return — nothing is filed or
          stored here.
        </p>
      </form>

      {loading && report === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : !error && report === null ? (
        <EmptyState
          title="No return computed yet"
          reason="Pick a period and basis above — the output/input tax boxes for that range show here. Nothing is filed or stored."
        />
      ) : report ? (
        <div className="grid gap-4">
          <div
            className="surface overflow-hidden rounded-2xl"
            data-testid="tax-return-summary"
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Tax charged on sales</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(report.output_vat_cents)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Tax paid on purchases</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(report.input_vat_cents)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold">Net tax owed</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {money(report.net_vat_cents)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <section
            aria-label="Return boxes"
            className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
            data-testid="tax-return-table"
          >
            <h3 className="bg-[var(--muted)] px-5 py-2 text-xs font-semibold tracking-widest uppercase text-[var(--muted-foreground)]">
              Boxes · {report.start_date} – {report.end_date} · {report.basis}
            </h3>
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Box</TableHead>
                    <TableHead>What it covers</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.boxes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        No boxes computed for this period.
                      </TableCell>
                    </TableRow>
                  ) : (
                    report.boxes.map((box) => (
                      <TableRow key={box.box}>
                        <TableCell className="font-mono text-xs">
                          {box.box}
                        </TableCell>
                        <TableCell>{box.label}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {money(box.amount_cents)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </section>

          <div
            className="stack-list grid gap-3 min-[64rem]:hidden"
            data-testid="tax-return-stack"
          >
            <p className="text-xs text-[var(--muted-foreground)]">
              Boxes · {report.start_date} – {report.end_date} · {report.basis}
            </p>
            {report.boxes.length === 0 ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                No boxes computed for this period.
              </p>
            ) : (
              report.boxes.map((box) => (
                <article
                  key={box.box}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                    <span className="record-title font-mono">{box.box}</span>
                    <span className="tabular-nums">
                      {money(box.amount_cents)}
                    </span>
                  </div>
                  <CardField label="What it covers">{box.label}</CardField>
                </article>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
